import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Button from '../../../ui/Button';
import Calendar from '../../../ui/Calendar';
import Chip from '../../../ui/Chip';
import Icon from '../../../ui/Icon';
import MoneyInput from '../../../ui/MoneyInput';
import SelectMenu from '../../../ui/SelectMenu';
import Sheet from '../../../ui/Sheet';
import { Conta } from '../../../servicos/contas/typeConta';
import { useAtualizacaoContexto } from '../../../contextos/AtualizacaoContexto';
import useAvisos from '../../../hooks/useAviso';
import { mensagemDeErro, TRANSFERENCIA_CONTAS } from '../../../utils/Utils';
import { deISO, paraISO, rotuloData } from '../../movimentacoes/transacaoForm';
import styles from './TransferirSheet.module.css';

const API_TRANSACOES = process.env.REACT_APP_API_URL + '/transacoes';

export type TransferirSheetProps = {
  aberto: boolean;
  onFechar: () => void;
  contas: Conta[];
};

/**
 * Transferência entre contas próprias: cria o PAR de transações (saída na
 * origem, entrada no destino) com o tipoPagamento especial que fica fora de
 * receitas/despesas das análises — é o mesmo dinheiro mudando de bolso.
 */
export default function TransferirSheet({ aberto, onFechar, contas }: TransferirSheetProps) {
  const { setAtualizar } = useAtualizacaoContexto();
  const { adicionarAviso } = useAvisos();

  const [origem, setOrigem] = useState('');
  const [destino, setDestino] = useState('');
  const [valor, setValor] = useState(0);
  const [data, setData] = useState(paraISO(new Date()));
  const [calendarioAberto, setCalendarioAberto] = useState(false);
  const [tentou, setTentou] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const dataRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    setOrigem('');
    setDestino('');
    setValor(0);
    setData(paraISO(new Date()));
    setTentou(false);
    setCalendarioAberto(false);
  }, [aberto]);

  // Fecha o calendário em clique fora.
  useEffect(() => {
    if (!calendarioAberto) return;
    const aoClicarFora = (evento: MouseEvent) => {
      if (dataRef.current && !dataRef.current.contains(evento.target as Node)) setCalendarioAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [calendarioAberto]);

  const opcoes = contas.map((c) => ({
    id: c.id?.toString() ?? '',
    rotulo: c.descricao,
    icone: 'account_balance'
  }));

  const erros = {
    origem: !origem ? 'Selecione a conta de origem.' : undefined,
    destino: !destino ? 'Selecione a conta de destino.' : origem === destino ? 'Origem e destino precisam ser diferentes.' : undefined,
    valor: valor <= 0 ? 'Informe o valor da transferência.' : undefined
  };
  const temErro = Object.values(erros).some(Boolean);
  const erroDe = (campo: keyof typeof erros) => (tentou ? erros[campo] : undefined);

  const transferir = async () => {
    setTentou(true);
    if (temErro || salvando) return;

    const nomeOrigem = contas.find((c) => c.id?.toString() === origem)?.descricao ?? 'conta';
    const nomeDestino = contas.find((c) => c.id?.toString() === destino)?.descricao ?? 'conta';
    const token = localStorage.getItem('token');
    const cabecalho = { headers: { Authorization: `Bearer ${token}` } };
    const base = { valor, data: deISO(data), tipoPagamento: TRANSFERENCIA_CONTAS };

    setSalvando(true);
    try {
      // Saída da origem primeiro; a entrada só é criada se a saída deu certo.
      await axios.post(
        API_TRANSACOES,
        { ...base, tipo: 'D', contaId: Number(origem), descricao: `Transferência para ${nomeDestino}` },
        cabecalho
      );
      await axios.post(
        API_TRANSACOES,
        { ...base, tipo: 'R', contaId: Number(destino), descricao: `Transferência de ${nomeOrigem}` },
        cabecalho
      );
      adicionarAviso(`Transferência de ${nomeOrigem} para ${nomeDestino} registrada.`, 'sucesso');
      setAtualizar((atual) => !atual);
      onFechar();
    } catch (err) {
      adicionarAviso(mensagemDeErro(err) ?? 'Não foi possível registrar a transferência.');
    } finally {
      setSalvando(false);
    }
  };

  const isoHoje = paraISO(new Date());

  return (
    <Sheet
      aberto={aberto}
      onFechar={onFechar}
      titulo="Transferir entre contas"
      subtitulo="Movimentação entre as suas contas, sem afetar receitas e despesas."
      largura={480}
      rodape={
        <>
          <Button variante="fantasma" onClick={onFechar}>
            Cancelar
          </Button>
          <Button variante="primaria" icone="swap_horiz" carregando={salvando} onClick={transferir}>
            Transferir
          </Button>
        </>
      }
    >
      <div className={styles.corpo}>
        <div className={styles.valorDestaque}>
          <MoneyInput valor={valor} onChange={setValor} autoFocus erro={erroDe('valor')} />
        </div>

        <div className={styles.linhaContas}>
          <SelectMenu
            label="Da conta"
            valor={origem}
            onChange={setOrigem}
            opcoes={opcoes.filter((opcao) => opcao.id !== destino)}
            placeholder="Origem"
            erro={erroDe('origem')}
          />
          <span className={styles.setaMeio}>
            <Icon nome="arrow_forward" tamanho={18} />
          </span>
          <SelectMenu
            label="Para a conta"
            valor={destino}
            onChange={setDestino}
            opcoes={opcoes.filter((opcao) => opcao.id !== origem)}
            placeholder="Destino"
            erro={erroDe('destino')}
          />
        </div>

        <div className={styles.secaoData}>
          <span className={styles.secaoTitulo}>Data</span>
          <div className={styles.dataLinha}>
            <Chip rotulo="Hoje" icone="today" ativo={data === isoHoje} onClick={() => setData(isoHoje)} />
            <div className={styles.dataInput} ref={dataRef}>
              <button
                type="button"
                className={styles.dataBotao}
                onClick={() => setCalendarioAberto((atual) => !atual)}
              >
                <Icon nome="event" tamanho={18} />
                <span>{rotuloData(data)}</span>
                <Icon nome="expand_more" tamanho={16} />
              </button>
              {calendarioAberto && (
                <div className={styles.calendarioPop}>
                  <Calendar
                    modo="unica"
                    inicio={data ? deISO(data) : new Date()}
                    onChange={(novaData) => {
                      setData(paraISO(novaData));
                      setCalendarioAberto(false);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.nota}>
          <Icon nome="info" tamanho={16} />
          Serão criados dois lançamentos com o selo "transferência": a saída na conta de origem e a
          entrada na de destino. Transferências não contam como receita nem despesa nas análises.
        </div>
      </div>
    </Sheet>
  );
}
